import {ConfigObjectType, enumContainer} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightFilterPanel,
  LightSelectedFiltersPane,
  LightSelectFilter,
  MerieSchema,
  ShortcutEnum,
  useFormatters,
  useOrganizationsByOrgSecurity,
  UserFilterDto,
} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';

import {
  DEFAULT_HOSPITAL_NETWORK_REPORT_FILTERS,
  DEFAULT_HOSPITAL_REPORT_FILTERS,
  type FiltersData,
  InterventionOverviewReportFilter,
  InterventionOverviewReportFilterDepartmentDetailType,
  ReportType,
} from './types';
import {useAppSelector} from '../../../store_hooks';

const computeOrganizationalLevelType = (departmentDetail) => {
  let newOrganizationLevelType: MerieSchema.components['schemas']['OrganizationLevelTypeEnum'] | undefined;
  if (departmentDetail) {
    newOrganizationLevelType = departmentDetail === 'DEPARTMENT' ? 'PARENT_DEPARTMENT' : 'DEPARTMENT';
  } else {
    newOrganizationLevelType = undefined;
  }
  return newOrganizationLevelType;
};

type DepartmentDetailOption = {
  id: string;
  label: string;
};

type HospitalReportFiltersProps = {
  reportType: ReportType;
  filters: InterventionOverviewReportFilter;
  onFiltersChange: (filters: Partial<InterventionOverviewReportFilter>) => void;
  savedFiltersOpen: boolean;
  onToggleSavedFilters: () => void;
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  config: ConfigObjectType;
  onSetFilter: (newFilter: InterventionOverviewReportFilter) => void;
  departmentDetail?: InterventionOverviewReportFilterDepartmentDetailType | null;
  setDepartmentDetail?: (newDepartmentDetail: InterventionOverviewReportFilterDepartmentDetailType | null) => void;
  defaultReportFilters?: FiltersData['report'];
  hasActiveFilter?: boolean;
};
export function normalizeInterventionOverviewReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}

export const HospitalReportFilters: React.FC<HospitalReportFiltersProps> = ({
  reportType,
  filters,
  onFiltersChange,
  savedFiltersOpen,
  onToggleSavedFilters,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  config,
  onSetFilter,
  departmentDetail,
  setDepartmentDetail,
  defaultReportFilters,
  hasActiveFilter,
}) => {
  const formatters = useFormatters();
  const {t, formatDate, tEnum} = formatters;

  const handleDateChange = (newValue: {startDate?: string; endDate?: string; shortcut?: ShortcutEnum | null} | null) => {
    onFiltersChange({
      dateFrom: newValue?.startDate,
      dateTo: newValue?.endDate,
      floatingShortcutName: newValue?.shortcut,
    });
  };

  const handleDepartmentDetailChange = useCallback(
    (newValue: InterventionOverviewReportFilterDepartmentDetailType | null) => {
      if (setDepartmentDetail) {
        setDepartmentDetail(newValue);
      }
      const newOrganizationLevelType = computeOrganizationalLevelType(newValue);
      if (newOrganizationLevelType !== filters.organizationLevelType) {
        onFiltersChange({
          organizationLevelType: newOrganizationLevelType,
        });
      }
    },
    [filters.organizationLevelType, onFiltersChange, setDepartmentDetail]
  );

  const handleItemTypeChange = (newValue: string) => {
    onFiltersChange({itemType: newValue as any});
  };

  const handleMeasureTypeChange = (newValue: string) => {
    onFiltersChange({measureType: newValue as any});
  };

  const handleOrganizationIdChange = (newValue: string) => {
    onFiltersChange({organizationId: newValue as any});
  };

  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  // const defaultReportFilters = DEFAULT_HOSPITAL_REPORT_FILTERS(filters.organizationId);
  const hospitalOptions = (organizations ?? [])
    .filter((v) => v.levelType === 'HOSPITAL')
    .map((v) => ({
      id: v.id,
      label: v.organizationLongName,
    }));

  const departmentDetailOptions = useMemo<DepartmentDetailOption[]>(() => {
    const organizationId = filters.organizationId != null ? +filters.organizationId : null;
    const departmentOptions: DepartmentDetailOption[] = [];
    const organizationParentDepartments = organizations
      .filter((v) => v.levelType === 'PARENT_DEPARTMENT')
      .filter((v) => v.parentId === organizationId);
    if (organizationParentDepartments.length > 0) {
      departmentOptions.push({
        id: 'DEPARTMENT',
        label: t('hospital.report.filter.departmentdetail.department'),
      });
    }
    departmentOptions.push({
      id: 'UNIT',
      label: t('hospital.report.filter.departmentdetail.unit'),
    });
    return departmentOptions;
  }, [filters.organizationId, organizations, t]);
  const isDepartmentDetailFilterDisabled = departmentDetailOptions.length === 1;
  useEffect(() => {
    if (reportType !== ReportType.DEPARTMENT || !setDepartmentDetail) return;
    const hasValidSelection = departmentDetail != null && departmentDetailOptions.some((opt) => opt.id === departmentDetail);
    if (!hasValidSelection && departmentDetailOptions.length > 0) {
      handleDepartmentDetailChange(departmentDetailOptions[0].id as InterventionOverviewReportFilterDepartmentDetailType);
    }
  }, [reportType, departmentDetail, departmentDetailOptions, handleDepartmentDetailChange, setDepartmentDetail]);

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('hospital.network.report.filter.date'),
          value: `${formatDate(filters.dateFrom ?? '')} - ${formatDate(filters.dateTo ?? '')}`,
          defaultValue: [defaultReportFilters?.dateFrom, defaultReportFilters?.dateTo, defaultReportFilters?.floatingShortcutName],
        },
        {
          name: 'itemType',
          label: t('hospital.network.report.filter.item'),
          value: tEnum('InterventionsReportDbParamItemType', filters.itemType ?? undefined),
          defaultValue: defaultReportFilters?.itemType,
        },
        ...(reportType === ReportType.DEPARTMENT
          ? [
              {
                name: 'organizationId',
                label: t('reports.filters.organizationId'),
                value: hospitalOptions.find((w) => String(w.id) === String(filters.organizationId))?.label,
                defaultValue: defaultReportFilters?.organizationId,
              },
            ]
          : []),
        {
          name: 'measureType',
          label: t('hospital.network.report.filter.aggregation'),
          value: tEnum('InterventionsReportDbParamMeasureType', filters.measureType ?? undefined),
          defaultValue: defaultReportFilters?.measureType,
        },
      ]}
      tableFilters={filters as Record<string, any>}
      changeTableFilters={(newTableFilters) => onFiltersChange(newTableFilters as Partial<InterventionOverviewReportFilter>)}
      formatters={formatters}
    />
  );
  return (
    <LightFilterPanel
      open={savedFiltersOpen}
      toggleOpen={onToggleSavedFilters}
      hasActiveFilter={hasActiveFilter}
      savedFiltersProps={{
        storageIdPrefix,
        component,
        userFiltersLoading,
        addedUserFilters,
        userFilters,
        changeUserFilter,
        filter: filters as Record<string, any>,
        setFilter: (newFilter) => onSetFilter(newFilter as InterventionOverviewReportFilter),
        config,
        normalizeForCompare: normalizeInterventionOverviewReportFiltersForCompare,
      }}
      selectedFilters={selectedFiltersPane}
    >
      <LightMonthRangeFilter
        config={{
          name: 'reportDate',
          ui: {label: 'hospital.network.report.filter.date'},
        }}
        value={
          filters.dateFrom && filters.dateTo
            ? {
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                shortcut: filters.floatingShortcutName,
              }
            : null
        }
        defaultValue={
          defaultReportFilters?.dateFrom && defaultReportFilters?.dateTo
            ? {
                startDate: defaultReportFilters?.dateFrom,
                endDate: defaultReportFilters?.dateTo,
                shortcut: defaultReportFilters?.floatingShortcutName,
              }
            : null
        }
        onChange={handleDateChange}
        isRemoveFilterButtonHidden
        showShortcuts
      />
      <LightSelectFilter
        config={{
          name: 'reportItem',
          ui: {label: 'hospital.network.report.filter.item'},
        }}
        options={sortByMessageKey(enumMap.InterventionsReportDbParamItemType ?? [])}
        maxWidth='362px'
        value={filters.itemType}
        onChange={handleItemTypeChange}
        isClearable={false}
      />
      {reportType === ReportType.DEPARTMENT && (
        <>
          <LightSelectFilter
            config={{
              name: 'organizationId',
              ui: {label: 'reports.filters.organizationId'},
            }}
            options={hospitalOptions}
            value={filters.organizationId}
            onChange={handleOrganizationIdChange}
            isClearable={false}
          />
          <LightSelectFilter
            config={{
              name: 'reportDepartmentDetail',
              ui: {label: 'hospital.report.filter.departmentdetail'},
            }}
            options={departmentDetailOptions}
            maxWidth='176px'
            value={departmentDetail}
            onChange={handleDepartmentDetailChange}
            isClearable={false}
            disabled={isDepartmentDetailFilterDisabled}
          />
        </>
      )}
      <LightSelectFilter
        config={{
          name: 'reportAggregation',
          ui: {label: 'hospital.network.report.filter.aggregation'},
        }}
        options={sortByMessageKey(enumMap.InterventionsReportDbParamMeasureType ?? []).reverse()}
        maxWidth='362px'
        value={filters.measureType}
        onChange={handleMeasureTypeChange}
        isClearable={false}
      />
    </LightFilterPanel>
  );
};
